import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, ExternalLink, ArrowLeft, ChevronRight, Instagram } from 'lucide-react';
import SEO from './SEO';
import { useManagedCollection, slugify } from '../admin/useManagedCollection';
import { GROCERY_STORES, grocerySlug, FEATURED_GROCERY_IDS } from './Grocery';
import SocialLinks, { countSocialLinks } from './SocialLinks';

const BASE_URL = 'https://dallasdesiscoop.com';

const FALLBACK_IMAGE =
  'https://images.unsplash.com/photo-1542838132-92c53300491e?w=1200&h=800&fit=crop';

function resolveMapsUrl(s: any) {
  if (s.mapsUrl && String(s.mapsUrl).trim()) return s.mapsUrl;
  const address = s.address as string | undefined;
  if (address && address.trim() && !address.trim().startsWith('@')) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address.trim())}`;
  }
  return '';
}

export default function GroceryDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const mc = useManagedCollection<any>('grocery', GROCERY_STORES, (s, i) =>
    slugify(s.name || String(i))
  );

  const entry = mc.items.find((s: any) => grocerySlug(s) === slug);

  if (mc.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-8 h-8 border-2 border-[#082f6d] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!entry) {
    return (
      <div
        className="min-h-screen bg-white flex flex-col items-center justify-center px-4"
        style={{ fontFamily: "'Poppins', sans-serif" }}
      >
        <SEO
          title="Grocery Store Not Found | Dallas Desi Scoop"
          description="This grocery store listing could not be found."
          canonical="/grocery"
          noindex
        />
        <p className="text-gray-500 text-lg mb-4">Grocery store not found.</p>
        <Link
          to="/grocery"
          className="text-[#082f6d] font-semibold hover:underline"
        >
          ← Back to Grocery
        </Link>
      </div>
    );
  }

  const isFeatured = FEATURED_GROCERY_IDS.has(entry.id) || FEATURED_GROCERY_IDS.has(grocerySlug(entry));
  const hasInstagram = !!entry.instagramUrl || !!entry.instagram;
  const resolvedMapsUrl = resolveMapsUrl(entry);
  const hasAnySocial = countSocialLinks(entry) > 0;
  const city = entry.location || 'DFW';

  // Type-aware products/services: stores tagged Fresh Meat / Halal get a
  // meat-market emphasis; every store gets the standard South Asian grocery list.
  const types: string[] = Array.isArray(entry.type)
    ? entry.type.map(String)
    : entry.type
      ? [String(entry.type)]
      : [];
  const isFreshMeat = types.includes('Fresh Meat');
  const isHalal = types.includes('Halal');

  const generalProducts = [
    'Indian and Pakistani spices',
    'basmati rice',
    'lentils and dals',
    'atta and flours',
    'fresh produce and vegetables',
    'frozen foods',
    'dairy',
    'Indian snacks',
    'sweets',
    'everyday grocery essentials',
  ];
  const products = isFreshMeat
    ? ['fresh meat', 'halal meat', 'freshly cut chicken, goat and lamb', ...generalProducts]
    : generalProducts;

  const storeKind = isFreshMeat
    ? isHalal
      ? 'halal meat market and South Asian grocery store'
      : 'fresh meat market and South Asian grocery store'
    : 'Indian and South Asian grocery store';

  const title = `${entry.name} in ${city}, TX | Indian & Pakistani Grocery Store | Dallas Desi Scoop`;
  const description = `${entry.name} is a ${storeKind} in ${city}, TX, serving the Dallas–Fort Worth Desi community. Find spices, rice, lentils, snacks, sweets, frozen foods, dairy${isFreshMeat ? ', fresh and halal meat' : ''}, address, directions, and contact info.`;
  const canonicalPath = `/grocery/${slug}`;

  // Internal linking: other grocery stores, same city first, then the rest.
  const relatedStores = mc.items
    .filter((s: any) => grocerySlug(s) !== slug)
    .sort((a: any, b: any) => {
      const aSame = a.location === entry.location ? 0 : 1;
      const bSame = b.location === entry.location ? 0 : 1;
      return aSame - bSame;
    })
    .slice(0, 4);

  const addressParts = (entry.address || '').split(',').map((p: string) => p.trim()).filter(Boolean);
  const streetAddress =
    addressParts.length >= 3 ? addressParts.slice(0, -2).join(', ') : undefined;
  const addressLocality =
    addressParts.length >= 2 ? addressParts[addressParts.length - 2] : city;

  const jsonLd = [
    {
      '@type': 'GroceryStore',
      name: entry.name,
      image: entry.image || FALLBACK_IMAGE,
      description: `${entry.name} is a ${storeKind} in ${city}, TX serving the Dallas–Fort Worth Desi community. Products include ${products.slice(0, 6).join(', ')}.`,
      address: !entry.address?.trim()?.startsWith('@')
        ? {
            '@type': 'PostalAddress',
            streetAddress,
            addressLocality: addressLocality || undefined,
            addressRegion: 'TX',
            addressCountry: 'US',
          }
        : undefined,
      url: entry.instagramUrl || entry.instagram || entry.website || `${BASE_URL}${canonicalPath}`,
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${BASE_URL}/` },
        { '@type': 'ListItem', position: 2, name: 'Grocery', item: `${BASE_URL}/grocery` },
        { '@type': 'ListItem', position: 3, name: entry.name, item: `${BASE_URL}${canonicalPath}` },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-[#f9fafb]" style={{ fontFamily: "'Poppins', sans-serif" }}>
      <SEO
        title={title}
        description={description}
        canonical={canonicalPath}
        ogImage={entry.image || FALLBACK_IMAGE}
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="relative w-full overflow-hidden" style={{ minHeight: '260px' }}>
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url("${entry.image || FALLBACK_IMAGE}")` }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(to bottom, rgba(8,47,109,0.75) 0%, rgba(8,47,109,0.55) 55%, rgba(8,47,109,0.88) 100%)',
          }}
        />

        <div className="relative z-10 max-w-[1000px] mx-auto px-4 pt-6 pb-10 flex flex-col">
          <motion.div
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4 }}
            className="self-start flex items-center gap-1.5 mb-5"
          >
            <Link
              to="/grocery"
              className="flex items-center gap-1.5 text-white/80 hover:text-white text-[12px] font-medium transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Grocery
            </Link>
            <ChevronRight className="w-3 h-3 opacity-60 text-white/60" />
            <span className="text-white/60 text-[12px] font-medium">{entry.name}</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-white text-[26px] sm:text-[36px] font-extrabold leading-tight tracking-tight"
            style={{ textShadow: '0 2px 8px rgba(0,0,0,0.4)' }}
          >
            {entry.name}
          </motion.h1>

          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.18 }}
            className="flex flex-wrap items-center gap-3 mt-3"
          >
            {entry.address && !entry.address.trim().startsWith('@') && (
              <span className="flex items-center gap-1 text-white/85 text-[13px]">
                <MapPin className="w-3.5 h-3.5 text-[#51ba4f]" />
                {entry.address}
              </span>
            )}
            {entry.address && (
              <>
                <span className="text-white/40">·</span>
                <span className="text-white/75 text-[13px]">Grocery Store</span>
              </>
            )}
          </motion.div>

          {isFeatured && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.24 }}
              className="flex items-center gap-2 mt-4"
            >
              <p className="text-[12px] font-semibold text-[#51ba4f] tracking-wide">
                Featured by TastebudsByAnubhi
              </p>
              {hasAnySocial && <SocialLinks item={entry} px={22} />}
            </motion.div>
          )}
        </div>
      </section>

      {/* Main content */}
      <div className="max-w-[1000px] mx-auto px-4 py-10 grid grid-cols-1 md:grid-cols-3 gap-8">
        <div className="md:col-span-2">
          <h2 className="text-[18px] font-bold text-gray-900 mb-3">About {entry.name}</h2>
          <p className="text-[14px] text-gray-600 leading-relaxed max-w-[60ch]">
            {entry.name} is a {storeKind} serving the Desi community across Dallas–Fort Worth
            {entry.location ? `, located in ${entry.location}, TX` : ''}.
            {entry.address && !entry.address.trim().startsWith('@') ? ` Located at ${entry.address}.` : ''}
            {isFreshMeat
              ? ' The store is known for fresh meat and halal meat alongside a full range of South Asian groceries.'
              : ' Shoppers can find everyday Indian and Pakistani groceries, specialty ingredients, and hard-to-find South Asian products.'}
          </p>

          <h2 className="text-[18px] font-bold text-gray-900 mt-8 mb-3">Products & Services</h2>
          <p className="text-[14px] text-gray-600 leading-relaxed max-w-[60ch] mb-3">
            {entry.name} offers {isFreshMeat ? 'fresh meat and halal meat along with ' : ''}a wide selection of groceries for the Dallas–Fort Worth Desi community:
          </p>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 max-w-[60ch]">
            {products.map((p) => (
              <li key={p} className="flex items-start gap-2 text-[13px] text-gray-600">
                <span className="w-1.5 h-1.5 rounded-full bg-[#51ba4f] shrink-0 mt-1.5" />
                <span className="capitalize">{p}</span>
              </li>
            ))}
          </ul>

          {!isFeatured && hasAnySocial && (
            <div className="mt-6">
              <SocialLinks item={entry} px={20} />
            </div>
          )}
        </div>

        <div
          className="bg-white rounded-xl border border-gray-100 p-5 h-fit"
          style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}
        >
          <h3 className="text-[13px] font-bold uppercase tracking-wide text-gray-400 mb-4">
            Contact & Info
          </h3>
          <div className="space-y-3 text-[13px] text-gray-700">
            <div className="flex items-start gap-2">
              {hasInstagram ? (
                <Instagram className="w-4 h-4 text-[#51ba4f] shrink-0 mt-0.5" />
              ) : (
                <MapPin className="w-4 h-4 text-[#51ba4f] shrink-0 mt-0.5" />
              )}
              <span>{entry.address || '[Coming soon]'}</span>
            </div>
          </div>
          {!!resolvedMapsUrl && (
            <a
              href={resolvedMapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 flex items-center justify-center gap-1.5 bg-[#082f6d] hover:bg-[#062454] text-white text-[13px] font-semibold py-2.5 rounded-lg transition-colors"
            >
              <ExternalLink className="w-4 h-4" />
              Get Directions
            </a>
          )}
        </div>
      </div>

      {/* Internal linking: related grocery stores */}
      {relatedStores.length > 0 && (
        <div className="max-w-[1000px] mx-auto px-4 pb-10">
          <h2 className="text-[18px] font-bold text-gray-900 mb-4">
            More Indian Grocery Stores in DFW
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {relatedStores.map((s: any) => (
              <Link
                key={grocerySlug(s)}
                to={`/grocery/${grocerySlug(s)}`}
                className="bg-white rounded-lg border border-gray-100 p-3 hover:shadow-md transition-shadow"
              >
                <p className="text-[13px] font-bold text-gray-900 leading-tight line-clamp-2">{s.name}</p>
                <p className="text-[11px] text-gray-500 mt-1">{s.location || 'DFW'}, TX</p>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Internal linking: guides & categories */}
      <div className="max-w-[1000px] mx-auto px-4 pb-12 flex flex-wrap items-center gap-x-6 gap-y-2 text-[13px]">
        <span className="text-gray-500 font-medium">Explore more:</span>
        <Link to="/blog/best-indian-grocery-stores-dfw" className="text-[#082f6d] font-semibold hover:underline">
          Best Indian Grocery Stores in DFW guide
        </Link>
        <Link to="/restaurants" className="text-[#082f6d] font-semibold hover:underline">
          Desi Restaurants in DFW
        </Link>
        <Link to="/grocery" className="text-[#082f6d] font-semibold hover:underline">
          All DFW Grocery Stores
        </Link>
      </div>
    </div>
  );
}
